import {
  ExtendedAutocomplete,
  ExtendedTypography,
  sortOptionsBySelected,
} from '@issuemanager/apps/client/components';
import {
  MAX_CHARACTERS_TO_SEARCH,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import {
  useInfiniteIssueSubtypes,
  type UseInfiniteIssueSubtypesParamsObject,
} from '@issuemanager/client/features/issue-subtypes';
import { useLocalizedIssueTypes } from '@issuemanager/client/features/issues';
import { IssueType } from '@issuemanager/constants';
import { IssueSubtypeDto } from '@issuemanager/routes';
import { Stack, Typography } from '@mui/material';
import { useState } from 'react';
import type { MultipleSelectorProps } from '../types';
import { useMultipleSelectorProps } from '../use-multiple-selector';
import { mergePaginatedDataAndValue } from '../utils';

export type IssueSubtypeMultipleSelectorProps = MultipleSelectorProps<
  IssueSubtypeDto[],
  IssueSubtypeDto
> &
  Pick<
    UseInfiniteIssueSubtypesParamsObject,
    'filterByIssueTypeManager' | 'filterByIssueTypes'
  >;

export function IssueSubtypeMultipleSelector(
  props: IssueSubtypeMultipleSelectorProps,
) {
  const {
    value,
    onChange,
    filterByIssueTypeManager,
    filterByIssueTypes,
    enabled: _enabled,
    ...rest
  } = props;
  const localizedIssueTypes = useLocalizedIssueTypes();

  const [filterByText, setFilterByText] = useState<string>('');
  const [partialValue, setPartialValue] = useState(value ?? []);

  const issueSubtypesQuery = useInfiniteIssueSubtypes({
    params: {
      sortByName: 'asc',
      filterByName: filterByText,
      filterByEnabled: true,
      filterByIssueTypeManager,
      filterByIssueTypes,
    },
    handleError: true,
  });

  const options = mergePaginatedDataAndValue(
    issueSubtypesQuery.data?.pages.map((c) => c.data) ?? [],
    value ?? [],
  );

  const autocompleteProps = useMultipleSelectorProps({
    options,
    setInputValue: (text) => setFilterByText(truncateSearchText(text)),
    query: issueSubtypesQuery,
    value,
    setValue: onChange,
    partialValue,
    setPartialValue,
  });

  return (
    <ExtendedAutocomplete
      {...autocompleteProps}
      options={sortOptionsBySelected(
        options,
        partialValue,
        autocompleteProps.isOptionEqualToValue,
      )}
      filterOptions={(value) => value}
      renderOption={(_, option) => (
        <Stack>
          <Typography variant='body1'>{option.name}</Typography>
          <ExtendedTypography variant='textSm'>
            {localizedIssueTypes[option.issueType?.name as IssueType]}
          </ExtendedTypography>
        </Stack>
      )}
      maxLength={MAX_CHARACTERS_TO_SEARCH}
      {...rest}
    />
  );
}
